import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Building2 } from "lucide-react";
import { monthLabel, currentMonthLocal } from "./format";

export function OverheadsDialog({ open, onClose, canEdit, onChanged, defaultRate, unassigned = {} }) {
  const [rows, setRows] = useState([]);
  const [fx, setFx] = useState({});
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ month: currentMonthLocal(), label: "", amount: "", currency: "EUR" });

  async function load() {
    setLoading(true);
    try {
      const [o, f] = await Promise.all([api.get("/pnl/overheads"), api.get("/pnl/fx-rates")]);
      setRows(o.data);
      setFx(Object.fromEntries(f.data.map((x) => [x.month, x.rate])));
    }
    catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { if (open) load(); }, [open]);

  async function saveRate(month, value) {
    const rate = value === "" ? null : parseFloat(value);
    if ((fx[month] ?? null) === rate) return;
    if (rate !== null && !(rate > 0)) return toast.error("Cursul trebuie să fie pozitiv");
    try {
      await api.put(`/pnl/fx-rates/${month}`, { rate });
      setFx((cur) => { const n = { ...cur }; if (rate) n[month] = rate; else delete n[month]; return n; });
      toast.success(rate ? `Curs ${monthLabel(month)}: 1 € = ${rate} RON` : `${monthLabel(month)} folosește cursul implicit`);
      onChanged?.();
    } catch (e) { toast.error(apiErr(e)); }
  }
  const rateOf = (m) => fx[m] || defaultRate || 0;
  const monthEur = (m) => byMonth[m].reduce((s, r) => s + (r.currency === "EUR" ? r.amount : rateOf(m) ? r.amount / rateOf(m) : 0), 0);

  async function add() {
    if (!form.month) return toast.error("Alege luna");
    if (!(parseFloat(form.amount) >= 0)) return toast.error("Completează suma");
    setBusy(true);
    try {
      await api.post("/pnl/overheads", { ...form, amount: parseFloat(form.amount) });
      toast.success("Regie adăugată");
      setForm({ ...form, label: "", amount: "" });
      await load(); onChanged?.();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  }

  async function remove(r) {
    if (!window.confirm(`Ștergi „${r.label}" din ${monthLabel(r.month)}?`)) return;
    try { await api.delete(`/pnl/overheads/${r.id}`); toast.success("Șters"); await load(); onChanged?.(); }
    catch (e) { toast.error(apiErr(e)); }
  }

  const byMonth = rows.reduce((m, r) => { (m[r.month] ||= []).push(r); return m; }, {});
  const months = Object.keys(byMonth).sort().reverse();

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="overheads-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl font-bold"><Building2 className="h-5 w-5 text-[#3d68b1]" /> Regie lunară a firmei</DialogTitle>
          <DialogDescription>Chirie, utilități, administrativ… Suma fiecărei luni se repartizează automat pe proiecte, proporțional doar cu orele pontate în luna respectivă.</DialogDescription>
        </DialogHeader>

        {canEdit && (
          <div className="surface-inset p-3 grid grid-cols-2 md:grid-cols-[170px_1fr_220px_auto] gap-3 items-end" data-testid="overhead-form">
            <label className="text-[11px] text-slate-500">Luna
              <input type="month" data-testid="overhead-month" value={form.month} onChange={(e) => setForm({ ...form, month: e.target.value })} className="pio-input w-full min-w-[160px]" /></label>
            <label className="text-[11px] text-slate-500">Denumire
              <input data-testid="overhead-label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="ex. Chirie sediu" className="pio-input w-full" /></label>
            <label className="text-[11px] text-slate-500 col-span-2 md:col-span-1">Sumă
              <div className="flex gap-1">
                <input type="number" step="any" min="0" data-testid="overhead-amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="pio-input w-full min-w-[120px] font-mono" />
                <select data-testid="overhead-currency" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className="pio-input w-[80px] shrink-0"><option>EUR</option><option>RON</option></select>
              </div></label>
            <button data-testid="overhead-save" onClick={add} disabled={busy}
              className="col-span-2 md:col-span-1 h-9 flex items-center justify-center gap-1.5 px-3 rounded-lg text-[12px] font-medium bg-[#3d68b1] text-white disabled:opacity-60">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Adaugă
            </button>
          </div>
        )}

        <div className="max-h-[50vh] overflow-y-auto mt-3">
          {loading ? <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
            : months.length === 0 ? <div className="py-10 text-center text-slate-400 text-sm" data-testid="overheads-empty">Nicio regie lunară introdusă încă.</div>
            : months.map((m) => (
              <div key={m} className="mb-3" data-testid={`overhead-month-${m}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap text-[12px] font-semibold text-slate-700 mb-1">
                  <span>{monthLabel(m)}</span>
                  <span className="flex items-center gap-3 flex-wrap">
                    <label className="flex items-center gap-1.5 font-normal text-slate-500" title="Cursul folosit pentru regia în RON din această lună">
                      1 € =
                      <input key={`${m}-${fx[m] ?? ""}`} type="number" step="0.0001" min="0" defaultValue={fx[m] ?? ""} disabled={!canEdit}
                        placeholder={defaultRate ? String(defaultRate) : "curs"} data-testid={`overhead-fx-${m}`}
                        onBlur={(e) => saveRate(m, e.target.value)} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
                        className="pio-input w-[92px] font-mono" />
                      RON {!fx[m] && <span className="text-[10px] text-slate-400">(implicit)</span>}
                    </label>
                    <span className="tabular-nums" data-testid={`overhead-month-total-${m}`}>{monthEur(m).toLocaleString("ro-RO", { maximumFractionDigits: 2 })} €</span>
                  </span>
                </div>
                {unassigned[m] != null && (
                  <div className="mb-1 text-[11px] text-amber-700 bg-amber-50 rounded-md px-2 py-1" data-testid={`overhead-unassigned-${m}`}>
                    Nerepartizată încă — nu există ore pontate în {monthLabel(m)}. Se va repartiza automat când apar pontaje.
                  </div>
                )}
                <div className="surface divide-y divide-slate-100">
                  {byMonth[m].map((r) => (
                    <div key={r.id} className="flex items-center justify-between px-3 py-2 text-[13px]" data-testid={`overhead-row-${r.id}`}>
                      <span className="text-slate-700">{r.label}</span>
                      <span className="flex items-center gap-3">
                        <span className="tabular-nums font-semibold">{(r.amount || 0).toLocaleString("ro-RO", { maximumFractionDigits: 2 })} {r.currency}</span>
                        {canEdit && <button data-testid={`overhead-delete-${r.id}`} onClick={() => remove(r)} className="text-slate-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
